import { useState } from "react";
import { CheckBox } from "../widgets/CheckBox";
import { ColumnAlign } from "../widgets/ColumnAlign";
import { IpaKeyboard } from "../widgets/IpaKeyboard";
import { IpaButtons } from "../widgets/IpaButtons";
import { ConversionRules } from "./conversionRules";
import { ChoiceList } from "../widgets/ChoiceList";
import { ipaMulti, ipaSingle } from "../widgets/ipaAlts";
import { PhonemeLengths } from "../examine/PhonemeLengths";
import { IpaType } from "../../phonemes/ipa";
import { ButtonStatusType, StatusButton } from "../widgets/StatusButton";

/** UI for creating rules to convert from custom orthography to IPA */
export function OrthoToIpaRules(props: {
  show: boolean,
  rules: ConversionRules,
  version: number,  /** unique key that changes when the rules change */
  rulesChanged: () => void,
  single: Record<string, string>, /** key -> ortho symbol */
  multi: Record<string, string[]>, /** key -> multiple ortho symbols */
  width: number,
}) {
  const [lastVersion, setLastVersion] = useState(props.version);
  // rules: ortho -> IPA
  const [showIpaKeyboard, setIpaKeyboard] = useState(false);
  const [showOrthoKeyboard, setOrthoKeyboard] = useState(false);
  const [ruleInsert, setRuleInsert] = useState<[string] | undefined>();
  const [ruleText, setRuleText] = useState('');
  // rules2: IPA -> IPA
  const [showRules2, setShowRules2] = useState(false);
  const [showIpaKeyboard2, setIpaKeyboard2] = useState(false);
  const [rule2Insert, setRule2Insert] = useState<[string] | undefined>();
  const [rule2Text, setRule2Text] = useState('');
  // stress rules
  const [ruleStress, setRuleStress] = useState('1');
  const [showCustomStress, setShowCustomStress] = useState(false);
  const [showStressIpa, setStressIpa] = useState(false);
  const [stressInsert, setStressInsert] = useState<[string] | undefined>();
  const [stressText, setStressText] = useState('');
  // preprocessing
  const [lowercase, setLowercase] = useState(true);
  const [punctuation, setPunctuation] = useState(true);
  // vowel length rules
  const [lengthMap, setLengthMap] = useState(props.rules.getLengthMap());
  const [showLength, setShowLength] = useState(false);
  // compiling
  const [pendingCompile, setPendingCompile] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const single = showIpaKeyboard ? undefined : showOrthoKeyboard ? props.single : {};
  const multi = showIpaKeyboard ? undefined : showOrthoKeyboard ? props.multi : {};
  const single2 = showIpaKeyboard2 ? undefined : {};
  const multi2 = showIpaKeyboard2 ? undefined : {};
  const buttonStatus: ButtonStatusType = pendingCompile ? 'pending'
    : errors.length === 0 ? 'success' : 'failure';

  if (lastVersion !== props.version) {
    setLastVersion(props.version);
    setPendingCompile(false);
    setErrors([]);

    setLowercase(props.rules.getPreLowerCase());
    setPunctuation(props.rules.getPrePunctuation());
    setRuleText(props.rules.getRulesAsString());
    setRuleStress(props.rules.getStressDefault()?.toString() ?? '');

    const rules2 = props.rules.getRules2AsString();
    setRule2Text(rules2);
    setShowRules2(rules2 !== '');

    setLengthMap(props.rules.getLengthMap());
    setShowLength(Object.getOwnPropertyNames(props.rules.getLengthMap()).length > 0);

    const customStress = props.rules.getCustomStressAsString();
    setStressText(customStress);
    setShowCustomStress(customStress !== '');
  }

  function changeIpaKeyboard(on: boolean) {
    setIpaKeyboard(on);
    if (on) setOrthoKeyboard(false);
    document.getElementById('ortho-rules')?.focus();
  }
  function changeOrthoKeyboard(on: boolean) {
    setOrthoKeyboard(on);
    if (on) setIpaKeyboard(false);
    document.getElementById('ortho-rules')?.focus();
  }
  function changeIpaKeyboard2(checked: boolean) {
    setIpaKeyboard2(checked);
    document.getElementById('ortho-rules2')?.focus();
  }

  function useRules() {
    const result = props.rules.setRulesAndCompile(ruleText, rule2Text, showCustomStress ? stressText : undefined);
    if (result !== true) {
      setErrors(result);
    } else {
      setErrors([]);
      props.rulesChanged();
    }
    setPendingCompile(false);
  }

  function changeLower(flag: boolean) {
    setLowercase(flag);
    props.rules.setPreLowerCase(flag);
    setPendingCompile(true);
  }
  function changePre(flag: boolean) {
    setPunctuation(flag);
    props.rules.setPrePunctuation(flag);
    setPendingCompile(true);
  }

  function changeRuleText(text: string) {
    setRuleText(text);
    setPendingCompile(true);
  }
  function changeRuleText2(text: string) {
    setRule2Text(text);
    setPendingCompile(true);
  }
  function changeRuleStress(str: string) {
    setRuleStress(str);
    props.rules.setStressDefault(str === 'none' ? undefined : Number.parseInt(str));
    setPendingCompile(true);
  }

  function changeStressText(text: string) {
    setStressText(text);
    setPendingCompile(true);
  }

  function changeLength(phoneme: IpaType, length: number) {
    const newMap: Record<IpaType, number> = {};
    Object.getOwnPropertyNames(lengthMap).forEach(n => newMap[n] = lengthMap[n]);
    newMap[phoneme] = length;
    setLengthMap(newMap); // so the pointer changes & the control is refreshed
    props.rules.setLengthMap(newMap);
    setPendingCompile(true);
  }
  function clearLengths() {
    setLengthMap({});
    props.rules.setLengthMap({});
    setPendingCompile(true);
  }

  if (!props.show) {
    return <></>;
  }

  return (<>
    <ColumnAlign width={props.width}>{[
      <span key={1}><StatusButton onClick={useRules} status={buttonStatus}>use rules</StatusButton></span>,
      <span key={2}>
        <CheckBox checked={lowercase} onChange={changeLower}>lowercase</CheckBox>
        &nbsp;&nbsp;
        <CheckBox checked={punctuation} onChange={changePre}>punctuation</CheckBox>
      </span>,
      <span key={3}>
        default stress syllable:&nbsp;
        <ChoiceList items={['none', '1', '2', '-2', '-1']} selectedItem={ruleStress} onSelect={changeRuleStress} />
      </span>,
    ]}</ColumnAlign>

    {errors.length > 0 ? <>{errors[0]}<br /></> : undefined}

    <ColumnAlign width={props.width}>{[
      <span key={1}>rules:</span>,
      <span key={2}>
        <CheckBox checked={showIpaKeyboard} onChange={changeIpaKeyboard}>use IPA keyboard</CheckBox>
        &nbsp;&nbsp;
        <CheckBox checked={showOrthoKeyboard} onChange={changeOrthoKeyboard}>use custom keyboard</CheckBox>
      </span>,
    ]}</ColumnAlign>
    {showIpaKeyboard ? <IpaButtons onClick={(s) => setRuleInsert([s])} ipa={true} width={props.width} /> : <></>}
    {showOrthoKeyboard ? <IpaButtons onClick={(s) => setRuleInsert([s])} single={props.single} multi={props.multi} width={props.width} /> : <></>}
    <IpaKeyboard id='ortho-rules' cols={80} rows={15}
      value={ruleText} onChange={changeRuleText} single={single} multi={multi} insert={ruleInsert} />

    <ColumnAlign width={props.width}>{[
      <span key={1}>
        <CheckBox checked={showRules2} onChange={setShowRules2}>rules2 (second pass):</CheckBox>
      </span>,
      <span key={2}>
        {!showRules2 ? undefined
          : <CheckBox checked={showIpaKeyboard2} onChange={changeIpaKeyboard2}>use IPA keyboard</CheckBox>}
      </span>,
    ]}</ColumnAlign>
    {!showRules2 ? undefined
      : <>
        {showIpaKeyboard2 ? <IpaButtons onClick={(s) => setRule2Insert([s])} ipa={true} width={props.width} /> : <></>}
        <IpaKeyboard id='ortho-rules2' cols={80} rows={15}
          value={rule2Text} onChange={changeRuleText2} single={single2} multi={multi2} insert={rule2Insert} />
        <br />
      </>
    }

    <ColumnAlign width={props.width}>{[
      <span key={1}>
        <CheckBox checked={showCustomStress} onChange={setShowCustomStress}>custom stress rules:</CheckBox>
      </span>,
      <span key={2}>
        {!showCustomStress ? undefined
          : <CheckBox checked={showStressIpa} onChange={setStressIpa}>use IPA keyboard</CheckBox>}
      </span>,
    ]}</ColumnAlign>
    {showCustomStress ? <>
      {showStressIpa ? <IpaButtons onClick={(s) => setStressInsert([s])} ipa={true} width={props.width} /> : <></>}
      <IpaKeyboard value={stressText} onChange={changeStressText} insert={stressInsert}
        single={showStressIpa ? ipaSingle : {}} multi={showStressIpa ? ipaMulti : {}}
        cols={80} rows={5} />
      <br />
    </> : undefined}

    <ColumnAlign width={props.width}>{[
      <span key={1}>
        <CheckBox checked={showLength} onChange={setShowLength}>custom vowel lengths:</CheckBox>
      </span>,
      <span key={2}>
        {!showLength ? undefined :
          <PhonemeLengths lengthMap={lengthMap} setLength={changeLength} clearLengths={clearLengths} />}
      </span>
    ]}</ColumnAlign>
  </>);
}
